import { useState, useCallback } from "react";
import type { Annotation } from "./annotationTypes";

export type DrawingTool =
  | "select"
  | "linear" | "radial" | "diameter" | "angular" | "ordinate"
  | "centerline"
  | "leader" | "balloon" | "text"
  | "gdt" | "surfaceFinish";

export function useAnnotationStore() {
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTool, setActiveTool] = useState<DrawingTool>("select");

  const addAnnotation = useCallback((ann: Annotation) => {
    setAnnotations((prev) => [...prev, ann]);
    setSelectedId(ann.id);
    setActiveTool("select");
  }, []);

  const updateAnnotation = useCallback((id: string, changes: Partial<Annotation>) => {
    setAnnotations((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...changes } as Annotation : a))
    );
  }, []);

  const deleteAnnotation = useCallback((id: string) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
    setSelectedId((prev) => (prev === id ? null : prev));
  }, []);

  const clearAnnotations = useCallback(() => {
    setAnnotations([]);
    setSelectedId(null);
  }, []);

  const selectedAnnotation = annotations.find((a) => a.id === selectedId) ?? null;

  return {
    annotations,
    selectedId,
    selectedAnnotation,
    activeTool,
    setActiveTool,
    addAnnotation,
    updateAnnotation,
    deleteAnnotation,
    clearAnnotations,
    setSelectedId,
  };
}
